import { createSelector } from '@reduxjs/toolkit';

import isPageCountOptionsEnabled from 'editor/src/store/hostSettings/selector/isPageCountOptionsEnabled';
import { ECOMMERCE_PAGE_COUNT_CONTROL_NAME } from 'editor/src/store/variants/constants';
import getECommercePageCountOptionIndex from 'editor/src/store/variants/helpers/getECommercePageCountOptionIndex';
import { ECommerceProduct } from 'editor/src/store/variants/types';

import getECommerceProduct from './getECommerceProduct';

// Until the host sends `enablePageCountOptions`: every page count the product sells, whatever the
// rest of the selection, on the option named exactly "Page count".
function getAllPageCounts(ecommerceProduct: ECommerceProduct): number[] | undefined {
  const pageCountControlIndex = ecommerceProduct.options.findIndex(
    (option) => option.name === ECOMMERCE_PAGE_COUNT_CONTROL_NAME,
  );
  if (pageCountControlIndex === -1) {
    return undefined;
  }

  return ecommerceProduct.variants
    .map((variant) => parseInt(variant.optionValues[pageCountControlIndex], 10))
    .filter((pageCount) => !Number.isNaN(pageCount))
    .sort((a, b) => a - b);
}

// The page counts the buyer can move the design to, ascending: the page-count values of the
// variants that match the selected variant on every other option. They define the grid the design
// must land on (adding/removing pages must hit one of these exactly, see
// changeEcommerceVariantPageCountOperation), and a count sold only in another size is left out so
// a page change never switches the size with it. Returns undefined when the product is not an
// ecommerce product, has no page-count option, or its selected variant is unknown: a page change
// then could not switch the variant, so the price would stop following the design.
// Memoised on the product: every spread thumbnail reads it on every store update.
const getECommercePageCounts = createSelector(
  [getECommerceProduct, isPageCountOptionsEnabled],
  (ecommerceProduct, pageCountOptionsEnabled): number[] | undefined => {
    if (!ecommerceProduct) {
      return undefined;
    }
    if (!pageCountOptionsEnabled) {
      return getAllPageCounts(ecommerceProduct);
    }

    const pageCountOptionIndex = getECommercePageCountOptionIndex(ecommerceProduct);
    if (pageCountOptionIndex === -1) {
      return undefined;
    }

    const selectedVariant = ecommerceProduct.variants.find(
      (variant) => variant.id === ecommerceProduct.selectedVariantId,
    );
    if (!selectedVariant) {
      return undefined;
    }

    const pageCounts = ecommerceProduct.variants
      .filter((variant) =>
        variant.optionValues.every(
          (value, index) => index === pageCountOptionIndex || value === selectedVariant.optionValues[index],
        ),
      )
      .map((variant) => parseInt(variant.optionValues[pageCountOptionIndex], 10))
      .filter((pageCount) => !Number.isNaN(pageCount));
    return Array.from(new Set(pageCounts)).sort((a, b) => a - b);
  },
);

export default getECommercePageCounts;
